/**
 * Spec Phase 4 Slice 4E — FrozenDrawingsDock.
 * Phase 7 Slice 7F — numeral-hover pulse + numeral-click open.
 * Phase 9 Slice 9B — pencil opens ProposeRevisionModal.
 * Phase 9 Slice 9C — pop-out viewer (TBD).
 *
 * Lists frozen drawings for the matter from the spec store's
 * drawingSummaries field (populated during hydrate via
 * derive_drawing_summaries — locked-key digest with fig_label,
 * summary_short, technical_anchors, extracted_numerals).
 *
 * Phase 7 Slice 7F: subscribes to the numeral event bus.
 *  - on numeral:hover → every figure card whose extracted_numerals
 *    contain the numeral pulses (s6-figure-pulse class)
 *  - on numeral:hover-out → pulse clears
 *  - on numeral:click → dock opens itself (setDockOpen) so the
 *    drafter can scan which figures place the clicked numeral
 *
 * Phase 9 Slice 9B: pencil icon next to the (still-disabled)
 * pop-out icon — opens the ProposeRevisionModal targeting the
 * figure. The modal POSTs a spec_revision_proposals row + sends
 * the drafter to Stage 5; this dock stays read-only.
 */
import React, { useEffect, useState } from 'react';
import { Image, ExternalLink, Pencil } from 'lucide-react';
import { useSpecStore } from '@/store/specStore';
import { subscribe, type NumeralEvent } from '@/lib/numeralEventBus';
import Dock from './Dock';
import ProposeRevisionModal, {
  type ProposeRevisionTargetDrawing,
} from './ProposeRevisionModal';

export default function FrozenDrawingsDock(): JSX.Element {
  const drawingSummaries = useSpecStore((s) => s.drawingSummaries);
  const setDockOpen = useSpecStore((s) => s.setDockOpen);
  const openFigurePopout = useSpecStore((s) => s.openFigurePopout);
  const [pulsingNumeral, setPulsingNumeral] = useState<string | null>(null);
  const [proposeTarget, setProposeTarget] =
    useState<ProposeRevisionTargetDrawing | null>(null);

  useEffect(() => {
    const unsub = subscribe((ev: NumeralEvent) => {
      if (ev.kind === 'numeral:hover') {
        setPulsingNumeral(ev.numeral);
      } else if (ev.kind === 'numeral:hover-out') {
        setPulsingNumeral((cur) => (cur === ev.numeral ? null : cur));
      } else if (ev.kind === 'numeral:click') {
        // Only auto-open when at least one frozen figure carries
        // the numeral — otherwise opening the dock is noise.
        const matches = drawingSummaries.some((d) =>
          d.extracted_numerals.includes(ev.numeral),
        );
        if (matches) setDockOpen('frozenDrawings', true);
      }
    });
    return () => {
      unsub();
    };
  }, [drawingSummaries, setDockOpen]);

  return (
    <Dock
      dockKey="frozenDrawings"
      icon={<Image className="h-3.5 w-3.5 text-zinc-500" aria-hidden />}
      title="Frozen drawings"
      pillCount={drawingSummaries.length}
      testId="spec-dock-frozen-drawings"
    >
      {proposeTarget && (
        <ProposeRevisionModal
          target={proposeTarget}
          onClose={() => setProposeTarget(null)}
        />
      )}
      {drawingSummaries.length === 0 ? (
        <p className="text-[11px] text-zinc-500">
          No frozen drawings yet. Spec unlocks when at least one
          drawing is frozen at Stage 5.
        </p>
      ) : (
        <ul className="space-y-2">
          {drawingSummaries.map((d) => {
            const isPulsing =
              pulsingNumeral !== null
              && d.extracted_numerals.includes(pulsingNumeral);
            return (
            <li
              key={d.drawing_figure_id}
              className={[
                'rounded-sm border border-zinc-200 bg-white p-2 transition-shadow',
                isPulsing ? 's6-figure-pulse' : '',
              ].join(' ')}
              data-testid={`spec-frozen-drawing-${d.fig_label}`}
              data-pulsing={isPulsing ? 'true' : undefined}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="font-mono text-[10px] text-zinc-500">
                  {d.fig_label ?? 'Untitled'}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    className="text-zinc-300 hover:text-zinc-600"
                    onClick={() => setProposeTarget({
                      kind: 'drawing',
                      drawing_figure_id: d.drawing_figure_id,
                      fig_label: d.fig_label ?? null,
                      summary_short: d.summary_short,
                    })}
                    aria-label={`Propose revision to ${d.fig_label ?? 'figure'}`}
                    title="Propose a revision (won't edit the drawing — sends a banner to Stage 5)"
                    data-testid={`spec-drawing-pencil-${d.fig_label}`}
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    className="text-zinc-300 hover:text-zinc-600"
                    onClick={() => openFigurePopout(d.drawing_figure_id)}
                    aria-label="Open drawing in pop-out viewer"
                    title="Open the figure in a floating pop-out"
                    data-testid={`spec-drawing-popout-${d.fig_label}`}
                  >
                    <ExternalLink className="h-3 w-3" />
                  </button>
                </div>
              </div>
              {d.summary_short && (
                <p className="mt-1 text-[11px] leading-relaxed text-zinc-700 line-clamp-2">
                  {d.summary_short}
                </p>
              )}
              {d.extracted_numerals.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {d.extracted_numerals.slice(0, 8).map((n) => (
                    <span
                      key={n}
                      className="s6-num"
                      data-num={n}
                    >
                      {n}
                    </span>
                  ))}
                  {d.extracted_numerals.length > 8 && (
                    <span className="text-[10px] text-zinc-400">
                      +{d.extracted_numerals.length - 8}
                    </span>
                  )}
                </div>
              )}
            </li>
          );
          })}
        </ul>
      )}
    </Dock>
  );
}
